import React, { Fragment, useState } from "react";
import { useFormikContext } from "formik";
import CTA from "components/CTA";
import classnames from "classnames";
import FeatureGroupForm from "components/FeaturesSection/FeatureGroupForm";
import FeatureGroupsPreviewTable from "components/FeaturesSection/FeatureGroupsPreviewTable";
import { addFeatureGroups } from "utils/feature";
import { prepareProductList } from "utils/product";
import { AUTO_ATTRS } from "components/Form/fields/constants";
import { getIconForUID } from "components/FormerEditor/utils";
import { ADD_FEATURE_GROUP } from "utils/analytics";
import Overlay from "components/Overlay";
import { useIsAuthed, useProductsPrices } from "utils/selectors";
import ModelSearchList from "components/ModelSearchList";
import ProductCard from "components/ProductCard";
import ProductCell from "components/ProductCell";
import { PRODUCT } from "utils/constants/models";
import {
  LIST_PRESENTATION,
  EXTENDED_OVERLAY_CONTENT,
  MODEL_SEARCH_CLASSES,
  BUTTON_TAGS
} from "utils/constants/ui";
import UnauthedPickerPrompt from "components/UnauthedPickerPrompt";
import HoverIconButton from "components/HoverIconButton";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import { PRICING_TABLE_PRODUCT_LIMIT_MAX } from "components/FormerEditor/common/constants";
import { useToasts } from "react-toast-notifications";
import SlimCTA from "components/SlimCTA";
import { FEATURE_GROUPS_FORM_KEYS } from "utils/constants/feature";
import { matchSorter } from "match-sorter";

const LIMIT_COPY = {
  HEADER: `Product limit reached`,
  SUBHEADER: `You can only select ${PRICING_TABLE_PRODUCT_LIMIT_MAX} products at a time. Click on any selected product to remove it.`
};

const FeatureGroupsDragList = ({ products, tracking, showOnMount = false }) => {
  const { addToast } = useToasts();
  const isAuthed = useIsAuthed();

  /**
   * Allow the user to toggle between preview and edit mode
   */
  const [showOverlay, setShowOverlay] = useState(showOnMount);

  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState("");
  const {
    values,
    touched,
    setFieldTouched,
    setFieldValue
  } = useFormikContext();
  const canAdjustProductLayout = Boolean(
    get(values, FEATURE_GROUPS_FORM_KEYS.ADJUST_PRODUCT_LAYOUT)
  );
  const productLayoutTouched = Boolean(
    get(touched, FEATURE_GROUPS_FORM_KEYS.PRODUCT_LAYOUT)
  );

  const { products: allProducts, prices: allPrices } = useProductsPrices();
  const preparedProducts = prepareProductList({
    products: allProducts,
    prices: allPrices
  });
  const hasNoProductsInLayout = values.productLayout.length === 0;
  const activeModelIds = values.productLayout.map(({ uid }) => uid);
  const maxReached = activeModelIds.length >= PRICING_TABLE_PRODUCT_LIMIT_MAX;

  const onSelect = (selection) => {
    const updatedProductLayout = cloneDeep(values.productLayout);
    const prodIndex = updatedProductLayout.findIndex(
      ({ uid }) => uid === selection
    );

    const isPresent = prodIndex > -1;
    const actionRestricted = !isPresent && maxReached;
    if (!actionRestricted) {
      if (isPresent) {
        updatedProductLayout.splice(prodIndex, 1);
      } else if (!maxReached) {
        updatedProductLayout.push({ uid: selection });
      }

      setFieldValue(
        FEATURE_GROUPS_FORM_KEYS.PRODUCT_LAYOUT,
        updatedProductLayout
      );
      if (!productLayoutTouched) {
        setFieldTouched(FEATURE_GROUPS_FORM_KEYS.PRODUCT_LAYOUT, true);
      }
    } else {
      addToast(`${LIMIT_COPY.HEADER}. ${LIMIT_COPY.SUBHEADER}`, {
        appearance: "info",
        autoDismiss: true
      });
    }
  };
  let insert = null;
  if (maxReached) {
    insert = (
      <UnauthedPickerPrompt
        icon="alert"
        header={LIMIT_COPY.HEADER}
        subheader={LIMIT_COPY.SUBHEADER}
      />
    );
  } else if (!isAuthed) {
    insert = (
      <UnauthedPickerPrompt
        icon="magic"
        header="Test products"
        subheader="Select products to include on your feature comparison table."
      />
    );
  }

  const overlayContent = (
    <ModelSearchList
      models={preparedProducts || []}
      query={{
        model: PRODUCT,
        keys: ["name", "id", "prices"]
      }}
      initialPresentation={LIST_PRESENTATION.LIST}
      customClasses={{
        [LIST_PRESENTATION.GRID]: {
          ...MODEL_SEARCH_CLASSES,
          list: {
            list: "grid-container-2 ma0 pa0 list",
            item: "w-100"
          }
        },
        [LIST_PRESENTATION.LIST]: { ...MODEL_SEARCH_CLASSES }
      }}
      components={{
        [LIST_PRESENTATION.GRID]: ProductCell,
        [LIST_PRESENTATION.LIST]: ProductCard
      }}
      onClick={onSelect}
      activeModelIds={activeModelIds}
      insert={insert}
    />
  );

  /**
   * Only order by title if there is a search query
   * - First time render ordering is the responsibility of the Form initial values factory
   * - Initial ordering should not be an implicit side effect from search results ordering
   */
  const filtered = search
    ? matchSorter(values.featureGroups, search, {
        keys: ["title"]
      })
    : values.featureGroups;

  const inputProps = {
    className:
      "f5 border-box input-reset ba pa2 db w-100 br2 lh-copy b--black-20 ",
    type: "text",
    name: "text",
    onChange: (evt) => {
      setSearch(evt.currentTarget.value);
    },
    value: search,
    ...AUTO_ATTRS,
    placeholder: "Search by feature group title"
  };

  const MoreIcon = getIconForUID("more");
  const SearchIcon = getIconForUID("search");

  let content;
  const hasNoFilteredContent = filtered.length === 0;

  if (hasNoFilteredContent) {
    content = (
      <div className="pt3">
        <CTA
          header="No feature groups match your search"
          subheader="Clear your search and try again"
          customClasses={{
            container: "br2 pa3 hairline-1 ba"
          }}
        />
      </div>
    );
  } else if (hasNoProductsInLayout) {
    content = (
      <div className="measure center mw8 w-100 pt4">
        <SlimCTA
          header="No products selected"
          subheader="Select some products and set which features are included with each them."
          customClasses={{
            container:
              "w-100 flex flex-column justify-between ba br2 hairline-1 pa3 items-start bg-white",
            actionsWrapper: "pt2"
          }}
          actions={[
            {
              copy: "Select products",
              tag: BUTTON_TAGS.BUTTON,
              onClick: () => setShowOverlay(true)
            }
          ]}
        />
      </div>
    );
  } else {
    content = (
      <div className="pt3">
        {canAdjustProductLayout ? (
          <div className="w-100 flex justify-end pb3">
            <div className="w-auto flex flex-row items-center ba hairline-1 br2">
              <HoverIconButton type="edit" onClick={() => setShowOverlay(true)}>
                <div className="fw5 f6 pr1">Select products</div>
              </HoverIconButton>
            </div>
          </div>
        ) : null}
        <FeatureGroupsPreviewTable
          featureGroups={filtered}
          products={products}
          tracking={tracking}
        />
      </div>
    );
  }

  let featureGroupControls;
  if (!hasNoProductsInLayout) {
    featureGroupControls = (
      <Fragment>
        <div className="flex flex-row w-100">
          <button
            type="button"
            onClick={() => setShowSearch(!showSearch)}
            className={classnames(
              "fw5 pa2 bbox input-reset bg-content--secondary pointer f6 dib flex-shrink-0 br--left bl bt bb hairline-1 br-0 outline-0",
              {
                "br--left br2": !showSearch,
                "brl2--top": showSearch
              }
            )}
            rel="noopener noreferrer"
          >
            <MoreIcon size={18} />
          </button>
          <FeatureGroupForm
            customClasses={{
              field:
                "f5 border-box input-reset ba pa2 db w-100 lh-copy b--black-20 br-0 bl-0"
            }}
            copy={{ title: "" }}
            placeholder="Add a new feature group"
            addFeatureGroups={addFeatureGroups({
              values,
              setFieldValue,
              setFieldTouched,
              tracking: {
                event: ADD_FEATURE_GROUP,
                data: { ...tracking }
              }
            })}
          />
        </div>
        {showSearch && (
          <div className="pa3 bg-content--secondary bl br hairline-1 br--bottom br2 bb flex flex-row items-center">
            <SearchIcon size={18} />
            <div className="pl2 w-100">
              <input {...inputProps} />
            </div>
          </div>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="ph3">
        {featureGroupControls}
        {content}
      </div>
      {showOverlay && overlayContent && (
        <Overlay
          hideOverlay={() => setShowOverlay(false)}
          customClasses={{ content: EXTENDED_OVERLAY_CONTENT }}
        >
          <div className="pt3">
            <div className="f3 fw6 ph3 pb2">Select product</div>
            {overlayContent}
          </div>
        </Overlay>
      )}
    </Fragment>
  );
};

export default FeatureGroupsDragList;
